// The client mirror of server/src/lib/londonTime.ts's londonWallClockInstant (Session AQS, S-2):
// the INSTANT at which a London WALL-CLOCK date and time occurs. Dependency-free (Intl.DateTimeFormat
// is available in every browser this platform supports), so this is a verbatim copy rather than a
// reimplementation -- the two must never drift, which is why only the one function crosses (the
// server file's day/week helpers have no client caller).
//
// WHY THIS EXISTS: SchedulingPage's drag-drop reschedule wrote a flat "day at 09:00 UTC" (`new
// Date(day.getTime() + 9 * 3600000)`), which is 09:00 GMT in winter but 10:00 BST in summer -- a
// defect, not a design choice (the file's own top comment says "Dates are handled in UTC
// throughout to match the backend", describing the DAY-BUCKETING, not the write time). A drop
// should always land at 09:00 LONDON local time, whatever the season.
const LONDON_WALL = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/London",
  hour12: false,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

// London's offset from UTC, in ms, AT `instant`: +3600000 during BST, 0 during GMT.
function londonOffsetMs(instant: Date): number {
  const p: Record<string, number> = {};
  for (const part of LONDON_WALL.formatToParts(instant)) {
    if (part.type !== "literal") p[part.type] = Number(part.value);
  }
  const asIfUtc = Date.UTC(p.year!, p.month! - 1, p.day!, p.hour! % 24, p.minute!, p.second!);
  return asIfUtc - instant.getTime();
}

/**
 * The INSTANT at which a London WALL-CLOCK date and time occurs. TWO PASSES, deliberately: the
 * offset has to be read AT the instant being resolved, but that instant is what is being
 * computed, so the wall clock is first reinterpreted as if it were UTC to get an approximation,
 * the offset is read there, and then re-read at the corrected instant -- a time within an hour of
 * a DST transition therefore resolves against the offset that actually applies to it.
 *
 * Returns null for a malformed date or time rather than an Invalid Date, so a caller cannot
 * persist NaN by forgetting to check.
 */
export function londonWallClockInstant(ymd: string, hhmm: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(hhmm)) return null;
  const asIfUtc = new Date(`${ymd}T${hhmm}:00.000Z`);
  if (Number.isNaN(asIfUtc.getTime())) return null;
  const firstPass = new Date(asIfUtc.getTime() - londonOffsetMs(asIfUtc));
  return new Date(asIfUtc.getTime() - londonOffsetMs(firstPass));
}
